import { FormItem } from "@freightsimple/ui-core/FormLayout/FormItem";
import { RadioGroup } from "@freightsimple/ui-core/Controls/RadioGroup";
import { ShowFormErrors } from "@freightsimple/ui-core/FormLayout/ShowFormErrors";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { TextInput } from "@freightsimple/ui-core/Controls/TextInput";
import dangerousGoodsData from "@/GeneratedFixtures/DangerousGoods.json";
import { dangerousGoodsDescribeHazardClass } from "@/Shared/DangerousGoods/describeHazardClass";
import FuzzySearch from "fuzzy-search";
import { useEffect, useState } from "react";

interface LookupFromDescriptionTabProps {
  initial: string;
  forceValidation: boolean;
  onLookup: (
    _dangerousUnNumber: string,
    _dangerousClassification: string,
    _dangerousDescription: string,
    _dangerousPackingGroup: string | undefined,
  ) => void;

  onResetForceValidation: () => void;
}

interface DangerGoodsDataItem {
  d: string;
  c: string;
  un: string;
  psn: string; // Proper Shipping Name
  p?: string | undefined;
  sp?: number[] | undefined; // Special provisions, once the JSON carries them
}

// A result's identity is the UN number *and* the description — one UN number
// can appear under several descriptions, so the UN number alone isn't unique.
function keyFor(item: DangerGoodsDataItem): string {
  return `${item.un}|${item.d}`;
}

function secondaryLineFor(item: DangerGoodsDataItem): string {
  const base = `${item.un}, ${dangerousGoodsDescribeHazardClass(item.c)}`;
  return item.p ? `${base}, Packing Group ${item.p}` : base;
}

export function LookupTabContent(props: LookupFromDescriptionTabProps) {
  const { forceValidation } = props;
  const [search, setSearch] = useState(props.initial);
  const [searchResults, setSearchResults] = useState<
    Array<DangerGoodsDataItem>
  >([]);
  const [selectedKey, setSelectedKey] = useState("");
  const [hazardClass, setHazardClass] = useState<string | undefined>();
  const [unNumber, setUnNumber] = useState<undefined | string>();

  function errorMessageForSearch() {
    if (search === "") {
      return `Enter search term`;
    }

    if (unNumber === undefined) {
      return `Select an item from the list`;
    }

    if (hazardClass === "F") {
      return "These goods are forbidden for transport. Unfortunately you cannot use FreightSimple to move them.";
    }

    return undefined;
  }

  const searcher = new FuzzySearch(dangerousGoodsData, ["d", "un"], {
    sort: true,
  });

  function onSearch(_search: string) {
    if (_search === "") {
      setSearch("");
      setSearchResults([]);
      props.onResetForceValidation();
    } else {
      const result = searcher.search(_search);
      setSearch(_search);
      setSearchResults(result.slice(0, 100));
      props.onResetForceValidation();
    }
  }

  useEffect(
    function () {
      if (search !== "") {
        onSearch(search);
      }
    },

    [props.initial],
  );

  function onSelect(key: string) {
    const selected = searchResults.find((r) => keyFor(r) === key);
    if (selected === undefined) {
      return;
    }
    setSelectedKey(key);
    setUnNumber(selected.un);
    setHazardClass(selected.c);
    props.onLookup(selected.un, selected.c, selected.psn, selected.p);
  }

  return (
    <ShowFormErrors show={forceValidation}>
      <Stack gap={2}>
        <FormItem label="Search" error={errorMessageForSearch()}>
          <TextInput
            value={search}
            onChange={(e) => {
              onSearch(e.target.value);
            }}
            placeholder="Enter 'UN Number' or search by name"
            data-testid="lookup-un-number-input"
          />
        </FormItem>
        <RadioGroup
          vertical
          value={selectedKey}
          onChange={onSelect}
          options={searchResults.map((r) => ({
            value: keyFor(r),
            label: r.d,
            description: secondaryLineFor(r),
          }))}
        />
      </Stack>
    </ShowFormErrors>
  );
}
